import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Database, Loader2, Play, Download, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { formatDateTime } from "../../lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_STYLE = {
  ok: { icon: CheckCircle2, cls: "text-[#0E6B3F]", label: "Ready" },
  scope_missing: { icon: AlertTriangle, cls: "text-[#6B5600]", label: "Needs scope" },
  error: { icon: XCircle, cls: "text-[#C23A00]", label: "Error" },
  no_token: { icon: XCircle, cls: "text-[#C23A00]", label: "No token" },
};

export default function HubSpotExport() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hubspot-export/status`);
      setStatus(r.data);
      if (r.data.running_job) { setRunning(true); poll(r.data.running_job.id); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load HubSpot export status");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const poll = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/hubspot-export/jobs/${jobId}`);
        setJob(r.data);
        if (r.data.status !== "running") {
          clearInterval(pollRef.current);
          setRunning(false);
          if (r.data.status === "complete") toast.success("HubSpot export complete");
          else toast.error("HubSpot export failed");
          load();
        }
      } catch { clearInterval(pollRef.current); setRunning(false); }
    }, 5000);
  };

  const runExport = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/hubspot-export/run`, {});
      setJob(r.data);
      toast.info("Export started — this can take several minutes for a full portal");
      poll(r.data.id);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "Could not start export");
    }
  };

  const download = (type, format) => {
    const url = `${API}/hubspot-export/download?type=${type}&format=${format}`;
    // Fetch as a blob (endpoint is admin-gated; axios sends the Bearer token globally)
    axios.get(url, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data]);
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = type === "all" ? "hubspot_history_archive.zip" : `hubspot_${type}.${format}`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Download failed"));
  };

  if (loading) return <div className="p-10 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const access = status?.access || {};
  const labels = status?.object_types || {};
  const counts = status?.summary?.counts || {};
  const total = status?.summary?.total || 0;
  const anyScopeMissing = Object.values(access).some((v) => v === "scope_missing");

  return (
    <div className="space-y-4" data-testid="hubspot-export">
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardHeader>
          <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
            <Database className="w-5 h-5 text-[#C23A00]" /> HubSpot History Backup
          </CardTitle>
          <p className="text-sm text-[#59554D]">
            Pull all your HubSpot calls, notes, quotes, meetings and tasks into EQUIPR before you turn HubSpot off.
            The history is matched to each prospect/client by company and shows on their record, and you can also
            download a full archive.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Per-type access */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="hubspot-export-access">
            {Object.keys(labels).map((ot) => {
              const st = ACCESS_STYLE[access[ot]] || ACCESS_STYLE.error;
              const Icon = st.icon;
              return (
                <div key={ot} className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3" data-testid={`hs-type-${ot}`}>
                  <div className="text-sm text-[#1A1A1A] font-medium">{labels[ot]}</div>
                  <div className={`text-xs flex items-center gap-1 mt-1 ${st.cls}`}>
                    <Icon className="w-3.5 h-3.5" /> {st.label}
                  </div>
                  <div className="text-[11px] text-[#8A857A] mt-1">{typeof counts[ot] === "number" ? `${counts[ot]} imported` : "—"}</div>
                </div>
              );
            })}
          </div>

          {anyScopeMissing && (
            <div className="rounded-lg border border-[#FFF7D1] bg-[#FFF7D1] p-3 text-xs text-[#59554D]" data-testid="hs-scope-notice">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#6B5600] shrink-0 mt-0.5" />
                <div>
                  <b className="text-[#1A1A1A]">Quotes need one more scope.</b> In HubSpot → Settings → Integrations →
                  Private Apps → your app → <b>Scopes</b>, tick <code className="text-[#6B5600]">crm.objects.quotes.read</code>,
                  save, copy the regenerated token into the backend <code>HUBSPOT_ACCESS_TOKEN</code>, then re-run.
                  Everything else can be exported right now.
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={runExport} disabled={running || !status?.token_configured}
              className="bg-[#C23A00] hover:bg-[#FFE9E0] text-[#1A1A1A] font-medium" data-testid="hs-run-export">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {running ? "Exporting…" : total > 0 ? "Re-run export" : "Run export"}
            </Button>
            <Button variant="outline" onClick={load} disabled={running}
              className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="hs-refresh">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            {status?.summary?.last_import && (
              <span className="text-xs text-[#8A857A]">Last import: {formatDateTime(status.summary.last_import)}</span>
            )}
          </div>

          {/* Live progress */}
          {running && job && (
            <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 text-sm space-y-2" data-testid="hs-progress">
              <div className="text-[#59554D] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#C23A00]" />
                {job.current === "index" ? "Building companies & contacts index…" : `Exporting ${labels[job.current] || job.current}…`}
              </div>
              {job.indexed?.companies != null && (
                <div className="text-xs text-[#8A857A]">Indexed {job.indexed.companies} companies · {job.indexed.contacts} contacts · {job.indexed.owners} owners</div>
              )}
              <div className="flex gap-3 flex-wrap text-xs">
                {Object.entries(job.counts || {}).map(([ot, n]) => (
                  <span key={ot} className="text-[#59554D]">{labels[ot] || ot}: <b>{typeof n === "number" ? n : n}</b></span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Downloads */}
      {total > 0 && (
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="hs-downloads">
          <CardHeader><CardTitle className="text-base text-[#1A1A1A] flex items-center gap-2"><Download className="w-4 h-4 text-[#8A6D00]" /> Download archive</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={() => download("all", "zip")}
                className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" data-testid="hs-download-zip">
                <Download className="w-4 h-4 mr-2" /> Full archive (ZIP)
              </Button>
              <span className="text-xs text-[#8A857A]">{total} records · one CSV per type + a complete JSON</span>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {Object.keys(labels).filter((ot) => (counts[ot] || 0) > 0).map((ot) => (
                <Button key={ot} size="sm" variant="outline" onClick={() => download(ot, "csv")}
                  className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid={`hs-download-${ot}`}>
                  {labels[ot]} CSV ({counts[ot]})
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
